import { MagicVariables } from '@/types';
import { RecommendationClickPayload, sendRecommendationClick } from './recommendations';
import { SendVideoClickParams } from './types';
import { RecommendationStore } from '@/stores/recommendation/recommendation';

export class BaseVideoClickTracker implements MagicVariables {
    $root: MagicVariables['$root'];
    $refs: MagicVariables['$refs'];
    videoItems: HTMLElement[];
    recommendationStore: RecommendationStore;

    constructor(recommendationStore: RecommendationStore) {
        this.recommendationStore = recommendationStore;
    }

    init($root: MagicVariables['$root'], $refs?: MagicVariables['$refs']) {
        this.$root = $root;
        this.$refs = $refs;
        this.getVideoItems();
        this.setupVideoItemClickListener();
        this.setupPopunderListener();
    }

    setupVideoItemClickListener() {
        this.videoItems.forEach((videoItem) => {
            videoItem.addEventListener('click', this.onVideoItemClick.bind(this));
        });
    }

    setupPopunderListener() {
        window.addEventListener('popunder_triggered', this.onPopunderTriggered.bind(this));
    }

    onPopunderTriggered(event: CustomEvent) {
        const videoItem = event.detail.target as HTMLElement;
        const trackingData = this.getTrackingData(videoItem);

        if (!this.isTrackingDataValid(trackingData)) {
            return;
        }

        this.recommendationStore.addItem({
            id: trackingData.videoId.toString(),
            recommendedBy: this.recommendationStore.getRecommendationId(),
        });
    }

    onVideoItemClick(event: MouseEvent) {
        const videoItem = event.currentTarget as HTMLElement;
        const trackingData = this.getTrackingData(videoItem);

        if (!this.isTrackingDataValid(trackingData)) {
            return;
        }

        const recommendationId = this.recommendationStore.getRecommendationId();

        this.recommendationStore.addItem({
            id: trackingData.videoId.toString(),
            recommendedBy: recommendationId,
        });

        this.handleVideoItemClick(trackingData, recommendationId);
    }

    // Default implementation - can be overridden by subclasses if needed
    getTrackingData(videoItem: HTMLElement): SendVideoClickParams {
        const itemPosition = this._getVideoItemPosition(videoItem);
        const videoId = this._getVideoItemId(videoItem);
        const pageNumber = 1;

        return {
            videoId,
            pageNumber,
            itemPosition,
        };
    }

    // Default implementation - can be overridden by subclasses if needed
    isTrackingDataValid(trackingData: SendVideoClickParams): boolean {
        if (trackingData.videoId < 0 || trackingData.itemPosition < 1) {
            return false;
        }

        return true;
    }

    // Default implementation - can be overridden by subclasses if needed
    getVideoItems() {
        this.videoItems = Array.from(this.$root.querySelectorAll('.js-video-item'));

        // .video-item and .js-video-item are still both used to identify video items.
        if (!this.videoItems.length) {
            this.videoItems = Array.from(this.$root.querySelectorAll('.video-item'));
        }
    }

    // Default implementation - can be overridden by subclasses if needed
    handleVideoItemClick(trackingData: SendVideoClickParams, recommendationId: string) {
        const payload: RecommendationClickPayload = {
            video_id: trackingData.videoId,
            page_number: trackingData.pageNumber,
            item_position: trackingData.itemPosition,
        };

        if (recommendationId) {
            payload.recommendation_id = recommendationId;
        }

        this.sendRecommendationClickEvent(payload);
    }

    handleRecommendationsState(videoId: string) {
        const recommendationId = this.recommendationStore.findSourceRecommendationId(videoId);

        if (!recommendationId) {
            this.reportMissingRecommendationId();
            return;
        }

        return recommendationId;
    }

    reportMissingRecommendationId() {
        console.error('No recommendationId has been found');
    }

    /**
     * Returns the position of the video item, starting from 1.
     *
     * @returns {number} - The position of the video item in the list.
     */
    _getVideoItemPosition(videoItem: HTMLElement): number {
        return Array.from(this.videoItems).indexOf(videoItem) + 1;
    }

    /**
     * Returns the data-id attribute value of the video item element.
     *
     * @param {HTMLElement} videoItem - The video item element.
     * @returns {number} - The video ID as a number.
     */
    _getVideoItemId(videoItem: HTMLElement): number {
        const videoId = videoItem.getAttribute('data-id');
        return Number(videoId);
    }

    /**
     * Send data to backend
     */
    sendRecommendationClickEvent(payload: RecommendationClickPayload) {
        sendRecommendationClick(payload);
    }
}
